import React, { memo, useMemo, useState, useEffect, useCallback } from "react"
import { css, Theme } from "@emotion/react"
import {
	APPLICATION,
	CreditCard,
	PaymentMethod,
	StripeBankAccount,
	StripeBankAccountStatus,
	useAuth,
	useCustomerPaymentMethods,
	useIsUser,
	UserId,
	useSites,
	useUserCanUse,
	useUserProfile,
} from "@ncs/ncs-api"
import { useShopContext } from "~/contexts"
import { AnimatedEntrance, ButtonEcomm, Paragraph, useChangeCallback } from "@ncs/web-legos"
import {
	SquareFormEcomm,
	NewBankAccountModalEcomm,
} from "~/views/account/components/billing-tab/components"
import { CreditCards } from "./CreditCards"
import { Radio } from "@material-ui/core"
import { FooterMessage } from "./FooterMessage/FooterMessage"
import { AddNewBankAccount } from "./ACH/AddNewBankAccount"
import { BankAccountAdded } from "./ACH/BankAccountAdded"
import { ListofBankAccounts } from "./ACH/ListofBankAccounts"

export interface PaymentSelectorEcommProps {
	paymentMethodValue: PaymentMethod | null
	storedCardValue: CreditCard | null
	bankAccountValue: StripeBankAccount | null
	onChangeMethod: (newValue: PaymentMethod | null) => void
	onChangeStoredCard: (newValue: CreditCard) => void
	onChangeBankAccount: (newValue: StripeBankAccount) => void
	hiddenMethods?: PaymentMethod[]
	onNewCard?: () => void
}

export const PaymentSelectorEcomm: React.FC<PaymentSelectorEcommProps> = memo(
	({
		paymentMethodValue = PaymentMethod.SQUARE_CARD,
		storedCardValue,
		onChangeMethod,
		onChangeStoredCard,
		onChangeBankAccount,
		hiddenMethods: hiddenMethodsProp = [],
		onNewCard,
	}) => {
		const canSeePaymentInfo = !useUserCanUse(APPLICATION.HidePaymentInfo)
		const [showNewBankAccountModal, setShowNewBankAccountModal] = useState(false)
		const [onAddSucess, setOnAddSucess] = useState(false)
		const [{ checkout }] = useShopContext()
		const [sites, sitesLoading] = useSites()
		const auth = useAuth()
		const [profile] = useUserProfile(auth.user?.id)
		const [toggleCreditCards, setToggleCreditCards] = useState(
			paymentMethodValue === PaymentMethod.SQUARE_CARD
		)
		const [selectedCardId, setSelectedCardId] = useState<string | null>(
			storedCardValue?.id ?? null
		)
		const [pendingCardId, setPendingCardId] = useState<string | null>(null)
		const canAddCard = onNewCard && canSeePaymentInfo
		const isDb = useIsUser(UserId.DrivenBrands)
		const [customerPaymentMethods, loading] = useCustomerPaymentMethods()
		const cards = useMemo(() => customerPaymentMethods?.cards ?? [], [customerPaymentMethods])
		const bankAccounts = customerPaymentMethods?.bankAccounts ?? []
		const noBankAccounts = bankAccounts.length === 0
		const noCards = cards.length === 0
		const defaultCard = cards.find(
			(c) => c.id === customerPaymentMethods?.defaultPaymentMethod?.id
		)
		const billToInfo = `${checkout.billTo?.name ?? ""} #${
			checkout.billTo?.customerNumber ?? ""
		}`
		const isGuest = profile?.isGuest || sites?.length == 0

		const [accountToVerify, _] = useState(false)
		const [selectedBankAccount, setSelectedBankAccount] = useState<StripeBankAccount | null>(
			null
		)

		const setStoredCardSelection = useCallback(
			(card: CreditCard) => {
				onChangeMethod(PaymentMethod.STORED_CARD)
				setSelectedCardId(card.id)
				onChangeStoredCard(card)
			},
			[onChangeMethod, onChangeStoredCard, setSelectedCardId]
		)

		const handleSelectCard = useCallback(
			(card: CreditCard) => {
				if (selectedCardId === card.id) {
					setSelectedCardId(null)
					onChangeMethod(PaymentMethod.SQUARE_CARD)
					return
				}
				setStoredCardSelection(card)
			},
			[setStoredCardSelection, selectedCardId, onChangeMethod]
		)

		const handleSelectSavedCard = useCallback(
			(card: CreditCard | null) => {
				if (!card) return
				setPendingCardId(card.id)
			},
			[setPendingCardId]
		)

		const handleSelectSquareCard = useCallback(() => {
			setSelectedCardId(null)
			onChangeMethod(PaymentMethod.SQUARE_CARD)
		}, [onChangeMethod, setSelectedCardId])

		useEffect(() => {
			if (!loading) {
				if (cards.length > 0) {
					onChangeMethod(PaymentMethod.STORED_CARD)
					setToggleCreditCards(true)
					setSelectedCardId(defaultCard?.id ?? cards[0].id)
				} else {
					onChangeMethod(null)
					setToggleCreditCards(false)
				}
			}
		}, [cards, loading, onChangeMethod, defaultCard])

		useEffect(() => {
			if (pendingCardId && !loading) {
				const pendingCard =
					cards.length > 0 ? cards.find((card) => card.id === pendingCardId) : null

				if (pendingCard) {
					setStoredCardSelection(pendingCard)
					setPendingCardId(null)
				}
			}
		}, [cards, loading, pendingCardId, setStoredCardSelection])

		useChangeCallback(
			customerPaymentMethods,
			(newPaymentMethods) => {
				if (newPaymentMethods && paymentMethodValue !== PaymentMethod.SQUARE_CARD) {
					const defaultSource = newPaymentMethods.defaultPaymentMethod?.source

					if (defaultSource === "card") {
						onChangeMethod(PaymentMethod.STORED_CARD)

						if (defaultCard) {
							onChangeStoredCard(defaultCard)
						} else if (newPaymentMethods.cards.length === 1) {
							onChangeStoredCard(newPaymentMethods.cards[0])
						}
					}

					if (defaultSource === "bank_account") {
						onChangeMethod(
							PaymentMethod.STORED_BANK_ACCOUNT ||
								selectedBankAccount?.status === "verified"
						)
						const defaultBankAccount = newPaymentMethods.bankAccounts.find(
							(account) => account.id === newPaymentMethods.defaultPaymentMethod?.id
						)

						if (
							defaultBankAccount &&
							defaultBankAccount.status === StripeBankAccountStatus.VERIFIED
						) {
							onChangeBankAccount(defaultBankAccount)
						} else if (newPaymentMethods.bankAccounts.length === 1) {
							onChangeBankAccount(newPaymentMethods.bankAccounts[0])
						}
					}
				}
			},
			{ callOnSetup: true }
		)

		const handleMethodChange = (newMethod: PaymentMethod) => {
			onChangeMethod(newMethod)
			if (newMethod === PaymentMethod.STORED_CARD && cards.length === 1) {
				onChangeStoredCard(cards[0])
			}
		}

		const hiddenMethods = useMemo(() => {
			if (sitesLoading || ((sites ?? []).length === 0 && !isGuest)) {
				return [...hiddenMethodsProp, PaymentMethod.CUSTOMER_ACCOUNT]
			} else {
				return hiddenMethodsProp
			}
		}, [hiddenMethodsProp, sites, sitesLoading, isGuest])

		const [lastCardPaymentMethod, setLastCardPaymentMethod] = useState<PaymentMethod | null>(
			null
		)

		useEffect(() => {
			if (
				paymentMethodValue === PaymentMethod.SQUARE_CARD ||
				paymentMethodValue === PaymentMethod.STORED_CARD
			) {
				setLastCardPaymentMethod(paymentMethodValue)
			}
		}, [paymentMethodValue])

		const newBankButton = () => (
			<ButtonEcomm
				variant="secondary-cta"
				onClick={() => setShowNewBankAccountModal(true)}
				containerProps={{ mt: 1, alignSelf: "center" }}
			>
				Add Bank Account
			</ButtonEcomm>
		)

		return (
			<div css={container}>
				<div css={methodPicker}>
					{/*	 CARDS */}
					{!isDb && isGuest && (
						<AnimatedEntrance show>
							<div className="radioItem">
								<Radio
									id="payment-method-SQUARE_CARD"
									name="payment-method"
									color="primary"
									value={
										cards.length > 0 ?
											PaymentMethod.STORED_CARD
										:	PaymentMethod.SQUARE_CARD
									}
									checked={
										paymentMethodValue === PaymentMethod.SQUARE_CARD ||
										paymentMethodValue === PaymentMethod.STORED_CARD
									}
									onChange={() =>
										handleMethodChange(
											lastCardPaymentMethod ?? PaymentMethod.SQUARE_CARD
										)
									}
								/>
								<label htmlFor="payment-method-SQUARE_CARD">
									Credit / Debit Card
								</label>
							</div>

							{(paymentMethodValue === PaymentMethod.SQUARE_CARD ||
								paymentMethodValue === PaymentMethod.STORED_CARD) && (
								<>
									{noCards && (
										<Paragraph
											css={{ marginLeft: "3.3rem", marginBottom: "1rem" }}
										>
											Add a card to pay your order.
										</Paragraph>
									)}

									{toggleCreditCards && !loading && (
										<CreditCards
											cards={cards}
											selectedCardId={selectedCardId}
											onSelectCard={handleSelectCard}
											isActive={
												paymentMethodValue === PaymentMethod.STORED_CARD
											}
										/>
									)}

									{!loading && (
										<div css={cardPayment}>
											{!noCards && (
												<Paragraph className="orAddNewCard">
													or add a new card
												</Paragraph>
											)}
											<div
												css={SquareCardPayment(
													paymentMethodValue ===
														PaymentMethod.SQUARE_CARD
												)}
											>
												{canAddCard && (
													<SquareFormEcomm
														handleSelectSquareCard={
															handleSelectSquareCard
														}
														handleSelectSavedCard={
															handleSelectSavedCard
														}
													/>
												)}
											</div>
											<FooterMessage brand="square" />
										</div>
									)}
								</>
							)}
						</AnimatedEntrance>
					)}

					{/*	 ACCOUNT */}
					{!isGuest && (
						<div>
							<Radio
								id="payment-method-CUSTOMER_ACCOUNT"
								name="payment-method"
								color="primary"
								value={PaymentMethod.CUSTOMER_ACCOUNT}
								checked={paymentMethodValue === PaymentMethod.CUSTOMER_ACCOUNT}
								onChange={() => handleMethodChange(PaymentMethod.CUSTOMER_ACCOUNT)}
							/>
							<label
								htmlFor="payment-method-CUSTOMER_ACCOUNT"
								css={{ marginLeft: "0.5rem" }}
							>
								Bill to Master Account
							</label>
							{PaymentMethod.CUSTOMER_ACCOUNT && (
								<Paragraph customColor="#374151" ml={3.4} mt={0}>
									{!isGuest ? billToInfo : null}
								</Paragraph>
							)}
						</div>
					)}

					{/*	BANK TRANSFER */}
					{!isDb && isGuest && (!loading || accountToVerify) && (
						<div>
							<Radio
								id="payment-method-STORED_BANK_ACCOUNT"
								name="payment-method"
								color="primary"
								value={PaymentMethod.STORED_BANK_ACCOUNT}
								checked={paymentMethodValue === PaymentMethod.STORED_BANK_ACCOUNT}
								onChange={() =>
									handleMethodChange(PaymentMethod.STORED_BANK_ACCOUNT)
								}
							/>
							<label htmlFor="payment-method-STORED_BANK_ACCOUNT">
								ACH Bank transfer
								<div>
									<AnimatedEntrance
										show={
											paymentMethodValue ===
												PaymentMethod.STORED_BANK_ACCOUNT &&
											!hiddenMethods.includes(
												PaymentMethod.STORED_BANK_ACCOUNT
											)
										}
									>
										{noBankAccounts ?
											<AddNewBankAccount
												addNewBankButton={newBankButton()}
											/>
										:	<ListofBankAccounts
												accountToVerify={accountToVerify}
												bankAccounts={bankAccounts}
												addNewBankButton={newBankButton()}
												onSelectAccount={(account) =>
													onChangeBankAccount(account)
												}
												defaultBankAccountId={
													checkout?.selectedBankAccount?.id
												}
											/>
										}
									</AnimatedEntrance>
									{!bankAccounts && (
										<ButtonEcomm
											variant="secondary-cta"
											onClick={() => setShowNewBankAccountModal(true)}
											containerProps={{ mt: 2 }}
										>
											Add Bank Account
										</ButtonEcomm>
									)}
									<NewBankAccountModalEcomm
										isOpen={showNewBankAccountModal}
										onClose={() => setShowNewBankAccountModal(false)}
										onAddSuccess={() => setOnAddSucess(true)}
									/>
									<BankAccountAdded
										isOpen={onAddSucess}
										onClose={() => setOnAddSucess(false)}
									/>
								</div>
							</label>
						</div>
					)}
				</div>
			</div>
		)
	}
)

const container = css`
	display: flex;
	flex-direction: column;
	width: 100%;
`
const cardPayment = css`
	margin-left: 3.3rem;

	p.orAddNewCard {
		margin-bottom: 1rem;
		margin-top: 1rem;
		color: #374151;
		font-size: 14px;
		font-weight: 400;
	}
`
const SquareCardPayment =
	(isSquareFocused: boolean | undefined = false) =>
	(theme: Theme) => css`
		margin-left: 3.3rem;

		div.sq-card-wrapper,
		div.squareFormulary {
			margin-left: -3.3rem;
		}

		.sq-card-iframe-container {
			border-width: ${isSquareFocused ? "2px !important" : "1px"};
			border-color: ${isSquareFocused ?
				theme.palette.colors.blue400
			:	theme.palette.colors.gray300} !important;
		}
		.sq-card-message:empty {
			height: 0;
			opacity: 0;
			margin-top: 0;
			margin-bottom: 0.5rem;
		}
		.sq-card-message:not(:empty) {
			height: auto;
			opacity: 1;
		}
	`

const methodPicker = (theme: Theme) => css`
	color: #00264b;
	font-size: 14px;
	font-weight: 500;
	padding-left: 3rem;

	${theme.breakpoints.down("xs")} {
		padding-left: 0;
		padding-right: 0;
	}
`
